/**
 * Section Analytics — GET /api/admin/analytics?days=7|30|90|365
 * (administrateurs). Tout est calculé côté API, en SQL, à l'heure de Tunis ;
 * chaque indicateur de période a son pendant `*_prev` (période précédente de
 * même durée).
 */

export type AnalyticsDays = 7 | 30 | 90 | 365;

export interface AnalyticsData {
  period: { days: AnalyticsDays; bucket: 'day' | 'week'; timezone: string; generated_at: string };
  rentals: {
    requests: number; requests_prev: number;
    /** Demandes déposées sur la période (et la précédente) finalement annulées ou retirées. */
    cancelled: number; cancelled_prev: number;
    completed: number; completed_prev: number;
    /** Loyers des locations terminées (total_price), sur la période, la précédente, et depuis le lancement. */
    completed_value: number; completed_value_prev: number; completed_value_all: number; completed_all: number;
    /** Service + protection payés par les locataires en plus du loyer. */
    renter_fees: number; renter_fees_prev: number; renter_fees_all: number;
    /** Ce qui revient à Dingo : frais locataires + commission − part rendue au propriétaire. */
    dingo_share: number; dingo_share_prev: number; dingo_share_all: number;
    /** Règlements reçus des propriétaires (Frais › Soldes). */
    settled: number; settled_all: number;
    avg_hours: number; no_shows: number;
    pending_now: number; accepted_now: number; ongoing_now: number;
  };
  funnel: {
    requested: number; accepted: number; started: number; completed: number;
    still_pending: number; still_accepted: number; cancelled: number;
  };
  cancellations: { reason: string; count: number }[];
  /** `value` : loyers + frais locataires des locations terminées dans la case ; `dingo` : la part de Dingo. */
  series: { date: string; requests: number; completed: number; value: number; dingo: number; signups: number }[];
  by_type: { type: string; requests: number; accepted: number; cancelled: number; completed: number; value: number; online: number; listings: number }[];
  top_vehicles: {
    id_vehicle: string; brand: string; model: string; vehicle_type: string;
    hourly_rate: number | null; daily_rate: number | null; is_available: boolean; is_suspended: boolean;
    owner_name: string | null; requests: number; accepted: number; completed: number; cancelled: number; viewers: number;
  }[];
  /** Par marque + modèle : annonces publiées / en ligne aujourd'hui, demandes de la période et leur issue. */
  by_model: {
    brand: string | null; model: string | null; vehicle_type: string;
    listings: number; online: number; requests: number; accepted: number; completed: number; cancelled: number;
  }[];
  top_owners: { owner_id: string; name: string | null; email: string | null; requests: number; accepted: number; completed: number; value: number }[];
  heatmap: { dow: number; slot: number; count: number }[];
  community: {
    signups: number; signups_prev: number; users_total: number; users_verified: number; active_users: number;
    new_listings: number; new_listings_prev: number; listings_online: number; listings_total: number;
    rating_avg: number; reviews: number; tickets: number; verifications_pending: number;
  };
  /** Cases de `cell_deg` degrés (coin sud-ouest) : demandes et terminées sur la période, annonces en ligne aujourd'hui. */
  zones: {
    cell_deg: number;
    cells: { south: number; west: number; requests: number; completed: number; listings: number }[];
  };
  /** Zones les plus actives, nommées par quartier (cases voisines du même quartier additionnées). */
  top_zones?: { name: string; requests: number; completed: number }[];
  /** Demandes par heure de prise en charge demandée, de 0 h à 23 h (heure de Tunis). */
  hours?: number[];
}

export const getAnalytics = async (token: string, days: AnalyticsDays): Promise<AnalyticsData> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/admin/analytics?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data.data as AnalyticsData;
};
